import { memo, useCallback } from "react";

// Helpers
import { classNames, useTranslate } from "helpers";

// Servisni sloj
import {
  FLEET_VEHICLE_STATUS_CONFIG,
  ROOT_STATUSES,
} from "services/api/fleet/vehicles/consts";
import { FleetVehicleStatusEnum } from "services/api/fleet/vehicles/types";

import { VEHICLE_STATUS_DESCRIPTIONS } from "./consts";
import { VehicleStatusPickerProps } from "./types";

/**
 * Naziv pod-statusa nosi i roditelja ("Aktivno - U najmu"), jer isti prijevod ide i u
 * značku i u tablice. U popisu je roditelj odmah iznad, pa se prikazuje samo razlika.
 */
const getSubStatusLabel = (label: string) => {
  const [, ...rest] = label.split(/\s+[-–]\s+/);

  return rest.length > 0 ? rest.join(" – ") : label;
};

/**
 * Prvi korak promjene statusa: spojeni popis statusa, pod-statusi uvučeni ispod roditelja.
 * Samo odabir; što status traži dalje, zna čarobnjak koji ga prikazuje.
 */
const VehicleStatusPicker: React.FC<VehicleStatusPickerProps> = (props) => {
  const { value, onChange, currentStatus } = props;

  const { t } = useTranslate();

  const handleStatusClick = useCallback(
    (status: FleetVehicleStatusEnum) => () => {
      onChange(status);
    },
    [onChange]
  );

  const renderStatusItem = (status: FleetVehicleStatusEnum, isSubStatus = false, isLastSubStatus = false) => {
    const config = FLEET_VEHICLE_STATUS_CONFIG[status];
    const isSelected = value === status;
    const isCurrent = currentStatus === status;
    const label = isSubStatus ? getSubStatusLabel(t(config.label)) : t(config.label);

    return (
      <button
        key={status}
        type="button"
        onClick={handleStatusClick(status)}
        className={classNames(
          "group relative w-full flex items-center gap-3 py-3 pr-4 text-left focus:outline-none",
          "transition-[box-shadow,background-color] duration-200",
          isSubStatus ? "pl-14" : "pl-4",
          // Odabrani redak nosi obrub izvana i iznad susjeda: prekriva njihove linije i rub
          // popisa, a svoju liniju skriva, pa uz plavi obrub nema sivog ruba.
          isSelected ? "z-10 ring-2 ring-blue-400 bg-blue-50 !border-transparent" : "bg-white hover:bg-slate-50"
        )}
      >
        {/* Pod-statusi su spojeni s roditeljem okomitom linijom. */}
        {isSubStatus && (
          <span
            className={classNames(
              "absolute left-[1.875rem] top-0 w-px bg-slate-200",
              isLastSubStatus ? "h-1/2" : "h-full"
            )}
          />
        )}
        {isSubStatus && <span className="absolute left-[1.875rem] top-1/2 w-[1.625rem] h-px bg-slate-200" />}

        <span
          className={classNames(
            "flex items-center justify-center rounded-full flex-shrink-0",
            isSubStatus ? "w-6 h-6" : "w-7 h-7",
            config.color
          )}
        >
          <span className={classNames("rounded-full", isSubStatus ? "w-1.5 h-1.5" : "w-2 h-2", config.dotColor)} />
        </span>

        <span className="min-w-0">
          <span className="flex items-center gap-2">
            <span
              className={classNames(
                "text-sm font-semibold",
                isSelected ? "text-blue-700" : "text-slate-800 group-hover:text-blue-700"
              )}
            >
              {label}
            </span>
            {isCurrent && (
              <span className="px-1.5 py-0.5 rounded-sm bg-slate-100 text-[0.65rem] font-semibold uppercase tracking-wide text-slate-500">
                Trenutno
              </span>
            )}
          </span>
          <span className="block text-xs text-slate-500 truncate">{VEHICLE_STATUS_DESCRIPTIONS[status]}</span>
        </span>

        <span
          className={classNames(
            "ml-auto w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all",
            isSelected ? "border-blue-500 bg-blue-500" : "border-slate-300 bg-white"
          )}
        >
          {isSelected && (
            <svg className="w-3 h-3 text-white" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M2 6l3 3 5-5" />
            </svg>
          )}
        </span>
      </button>
    );
  };

  return (
    <div className="flex flex-col rounded-sm border border-slate-300 divide-y divide-slate-200 bg-white">
      {ROOT_STATUSES.flatMap((status) => {
        const subStatuses = FLEET_VEHICLE_STATUS_CONFIG[status].subStatuses ?? [];

        return [
          renderStatusItem(status),
          ...subStatuses.map((subStatus, index) =>
            renderStatusItem(subStatus, true, index === subStatuses.length - 1)
          ),
        ];
      })}
    </div>
  );
};

export default memo(VehicleStatusPicker);
